import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { RotateCcw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { BdrLead } from '@/types'
import { BdrBankTableFilters } from '@/components/BdrPanelV2/BdrBankTableFilters'
import { BdrSortIcon, formatBdrDate, getBdrInitials } from '@/components/BdrPanelV2/bdrTableShared'

interface BdrBankTableProps {
  onSelectLead?: (lead: BdrLead) => void
}

export function BdrBankTable({ onSelectLead }: BdrBankTableProps) {
  const [leads, setLeads] = useState<BdrLead[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterSegmento, setFilterSegmento] = useState('')
  const [filterPlataforma, setFilterPlataforma] = useState('')
  const [filterOrigem, setFilterOrigem] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [sortField, setSortField] = useState<string>('createdAt')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const pageSize = 10

  const fetchBankLeads = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = {
        page,
        pageSize,
        search,
        sortField,
        sortDir,
      }
      if (filterSegmento) params.segmento = filterSegmento
      if (filterPlataforma) params.plataforma = filterPlataforma
      if (filterOrigem) params.origem = filterOrigem

      const resp = await api.get('/bdr/migrated-lost', { params })
      setLeads(resp.data.data ?? [])
      setTotalPages(resp.data.totalPages ?? 1)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar leads do banco'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, search, filterSegmento, filterPlataforma, filterOrigem, sortField, sortDir])

  useEffect(() => {
    fetchBankLeads()
  }, [fetchBankLeads])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const handleRecover = async (leadId: string) => {
    try {
      await api.post(`/bdr/recover/${leadId}`)
      toast.success('Lead recuperado com sucesso!')
      fetchBankLeads()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao recuperar lead'
      toast.error(message)
    }
  }

  const resetPage = () => setPage(1)

  return (
    <div className="space-y-4">
      <BdrBankTableFilters
        search={search}
        filterSegmento={filterSegmento}
        filterPlataforma={filterPlataforma}
        filterOrigem={filterOrigem}
        onSearchChange={(value) => {
          setSearch(value)
          resetPage()
        }}
        onSegmentoChange={(value) => {
          setFilterSegmento(value)
          resetPage()
        }}
        onPlataformaChange={(value) => {
          setFilterPlataforma(value)
          resetPage()
        }}
        onOrigemChange={(value) => {
          setFilterOrigem(value)
          resetPage()
        }}
      />

      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-white/10 animate-pulse" />
                <div className="flex-1 h-4 bg-white/[0.05] rounded animate-pulse" />
                <div className="w-24 h-4 bg-white/[0.05] rounded animate-pulse" />
                <div className="w-20 h-4 bg-white/[0.05] rounded animate-pulse" />
                <div className="w-16 h-4 bg-white/[0.05] rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center">
            <Trash2 size={32} className="mx-auto mb-3 text-text-muted" />
            <p className="text-sm text-text-muted">Nenhum lead no banco</p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-text-muted">
                    Empresa
                  </th>
                  <th
                    className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-text-muted cursor-pointer hover:text-text-primary transition-colors"
                    onClick={() => handleSort('segmento')}
                  >
                    <span className="flex items-center gap-1">
                      Segmento <BdrSortIcon field="segmento" sortField={sortField} sortDir={sortDir} inactiveUsesUp />
                    </span>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-text-muted cursor-pointer hover:text-text-primary transition-colors"
                    onClick={() => handleSort('plataforma')}
                  >
                    <span className="flex items-center gap-1">
                      Plataforma <BdrSortIcon field="plataforma" sortField={sortField} sortDir={sortDir} inactiveUsesUp />
                    </span>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-text-muted cursor-pointer hover:text-text-primary transition-colors"
                    onClick={() => handleSort('origem')}
                  >
                    <span className="flex items-center gap-1">
                      Origem <BdrSortIcon field="origem" sortField={sortField} sortDir={sortDir} inactiveUsesUp />
                    </span>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-text-muted cursor-pointer hover:text-text-primary transition-colors"
                    onClick={() => handleSort('createdAt')}
                  >
                    <span className="flex items-center gap-1">
                      Data <BdrSortIcon field="createdAt" sortField={sortField} sortDir={sortDir} inactiveUsesUp />
                    </span>
                  </th>
                  <th className="text-right px-4 py-3 text-[10px] font-black uppercase tracking-widest text-text-muted">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, idx) => (
                  <motion.tr
                    key={lead.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={() => onSelectLead?.(lead)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
                          <span className="text-accent font-bold text-xs">{getBdrInitials(lead.empresa)}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">{lead.empresa}</p>
                          <p className="text-xs text-text-muted">{lead.contato}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{lead.segmento || '-'}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{lead.plataforma || '-'}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{lead.origem || '-'}</td>
                    <td className="px-4 py-3 text-sm text-text-muted">{formatBdrDate(lead.dataEntrada)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRecover(lead.id)
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent/10 border border-accent/20 text-accent text-xs font-medium hover:bg-accent/20 transition-colors"
                        title="Recuperar lead"
                      >
                        <RotateCcw size={12} />
                        Recuperar
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 px-4 py-4 border-t border-white/[0.06]">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-sm text-text-secondary hover:bg-white/[0.06] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      p === page
                        ? 'bg-accent text-white'
                        : 'bg-white/[0.03] border border-white/[0.06] text-text-secondary hover:bg-white/[0.06]'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-sm text-text-secondary hover:bg-white/[0.06] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Próximo
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
